import React, { useState, useEffect, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Loader2, X, Package, Globe, Building2 } from 'lucide-react';
import inventoryService from '../services/inventoryService';
import InventoryList from './InventoryList';
import ClientSelector from './ClientSelector';
import InventoryHistoryDialog from './InventoryHistoryDialog';

/**
 * ProductInventoryDrawer - 在Drawer中展示特定产品的库存详情
 * 
 * 功能：
 * - 70vw 宽度的侧边抽屉
 * - 按产品ID过滤显示库存详情
 * - 支持客户上下文切换
 * - 显示库存列表
 */
const ProductInventoryDrawer = ({ open, onClose, productCode, productName }) => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [excludeZeroStock, setExcludeZeroStock] = useState(false);
  
  // Client context state
  const [selectedClientId, setSelectedClientId] = useState(() => {
    return localStorage.getItem('inventorySelectedClient') || 'global';
  });
  const [selectedClient, setSelectedClient] = useState(null);
  
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 0
  });
  
  const [sort, setSort] = useState({ by: 'name', order: 'asc' });
  
  // History dialog state
  const [historyDialog, setHistoryDialog] = useState({ open: false, product: null });

  // Load inventory data for the specific product
  const loadInventory = useCallback(async () => {
    if (!productCode) return;
    
    try {
      setLoading(true);
      setError('');

      const currentClientId = selectedClientId === 'global' ? null : selectedClientId;
      
      // Load inventory filtered by product_id
      const response = await inventoryService.getInventory(
        pagination.page,
        pagination.page_size,
        sort.by,
        sort.order,
        { 
          product_id: productCode,
          include_reservations: true 
        },
        currentClientId,
        excludeZeroStock
      );

      setInventory(response.inventory || []);
      setPagination(prev => ({
        ...prev,
        ...response.pagination
      }));
    } catch (err) {
      setError(err.response?.data?.error || '加载库存失败');
    } finally {
      setLoading(false);
    }
  }, [productCode, pagination.page, pagination.page_size, sort.by, sort.order, selectedClientId, excludeZeroStock]);

  // Load data when drawer opens
  useEffect(() => {
    if (open && productCode) {
      loadInventory();
    }
  }, [open, productCode, loadInventory]);

  // Reset page to 1 when client changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [selectedClientId, excludeZeroStock]);

  // Client selection handler
  const handleClientChange = useCallback((clientId, clientData) => {
    const newClientId = clientId || 'global';
    setSelectedClientId(newClientId);
    setSelectedClient(clientData);

    // Persist client selection
    if (newClientId === 'global') {
      localStorage.removeItem('inventorySelectedClient');
    } else {
      localStorage.setItem('inventorySelectedClient', newClientId);
    }
  }, []);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = useCallback((property) => {
    const isAsc = sort.by === property && sort.order === 'asc';
    setSort({ by: property, order: isAsc ? 'desc' : 'asc' });
  }, [sort.by, sort.order]);

  const handleViewHistory = useCallback((product) => {
    setHistoryDialog({ open: true, product });
  }, []);

  const handleHistoryDialogClose = useCallback(() => {
    setHistoryDialog({ open: false, product: null });
  }, []);

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent 
          side="right" 
          className="w-[70vw] max-w-none p-0 overflow-hidden flex flex-col"
        >
          <SheetHeader className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-primary" />
                  库存详情
                </SheetTitle>
                <SheetDescription>
                  产品: <span className="font-medium text-foreground">{productName || productCode}</span>
                  {productName && productCode !== productName && (
                    <span className="text-muted-foreground ml-2">({productCode})</span>
                  )}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-auto p-6">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription className="flex items-center justify-between">
                  {error}
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                    <X className="h-4 w-4" />
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Client Context Selector */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  {selectedClientId === 'global' ? (
                    <Globe className="h-5 w-5 text-primary" />
                  ) : (
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                  )}
                  <h2 className="text-lg font-semibold">库存视图上下文</h2>
                </div>

                <ClientSelector
                  selectedClientId={selectedClientId}
                  onClientChange={handleClientChange}
                  showGlobalOption={true}
                  helperText={
                    selectedClientId === 'global'
                      ? '显示所有客户的库存数据'
                      : `显示客户 ${selectedClient?.name || selectedClientId} 的专属库存数据`
                  }
                />

                {selectedClientId !== 'global' && selectedClient && (
                  <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-md">
                    <p className="text-sm text-primary flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      <strong>客户上下文:</strong> {selectedClient.name}
                      {selectedClient.client_code && ` (${selectedClient.client_code})`}
                      - 显示该客户的专属产品属性、定价和库存数据
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Filter Options */}
            <div className="flex justify-end items-center gap-2 mb-4">
              <Switch
                id="exclude-zero-stock-drawer"
                checked={excludeZeroStock}
                onCheckedChange={setExcludeZeroStock}
              />
              <Label htmlFor="exclude-zero-stock-drawer" className="text-sm cursor-pointer">
                隐藏零库存
              </Label>
            </div>

            {/* Inventory List */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    <InventoryList
                      inventory={inventory}
                      loading={loading}
                      sort={sort}
                      onSort={handleSort}
                      onClearAllFilters={() => {}}
                      debouncedGlobalSearch=""
                      onViewHistory={handleViewHistory}
                      onRefresh={loadInventory}
                      selectedClientId={selectedClientId}
                      selectedClient={selectedClient}
                    />

                    {pagination.total_pages > 1 && (
                      <div className="flex justify-center py-4 border-t">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page <= 1}
                          >
                            上一页
                          </Button>
                          <span className="text-sm text-muted-foreground px-4">
                            第 {pagination.page} / {pagination.total_pages} 页
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page >= pagination.total_pages}
                          >
                            下一页
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Result count */}
            <p className="mt-4 text-sm text-muted-foreground">
              显示 {inventory.length} / {pagination.total_count} 个库存项目
            </p>
          </div>
        </SheetContent>
      </Sheet>

      {/* Inventory History Dialog */}
      <InventoryHistoryDialog
        open={historyDialog.open}
        onClose={handleHistoryDialogClose}
        productId={historyDialog.product?.product_id || historyDialog.product?.product_code}
        productInfo={historyDialog.product}
        selectedClientId={selectedClientId}
      />
    </>
  );
};

export default ProductInventoryDrawer;
